/**
 * Listing status derivation.
 *
 * The realtor UI shows a *derived* status label that combines the raw
 * `status` enum with the `isPublic` flag: an `active` listing is "Live"
 * only when published, otherwise it's a "Draft". Centralising the
 * derivation here keeps the status badge and the listings-list filter
 * chips in lock-step — they bucket every listing into exactly one of the
 * five labels below, so chip counts partition cleanly (no double-count of
 * an active-but-unpublished listing).
 */

import type { Listing } from "@/features/realtor/services/listings-api.service";

export type ListingStatusBucket =
	| "live"
	| "draft"
	| "under_negotiation"
	| "sold"
	| "withdrawn";

/**
 * Order matters and mirrors the original status-badge branching: sold /
 * withdrawn / under_negotiation are terminal enum states; everything else
 * ("active") splits on `isPublic`.
 */
export function deriveListingStatusBucket(
	listing: Pick<Listing, "status" | "isPublic">,
): ListingStatusBucket {
	if (listing.status === "sold") return "sold";
	if (listing.status === "withdrawn") return "withdrawn";
	if (listing.status === "under_negotiation") return "under_negotiation";
	return listing.isPublic ? "live" : "draft";
}

/** Stable display order for the filter chips. */
export const LISTING_STATUS_BUCKETS: readonly ListingStatusBucket[] = [
	"live",
	"draft",
	"under_negotiation",
	"sold",
	"withdrawn",
] as const;

/** i18n key (under `realtor.listings.status`) for a given bucket. */
export function listingStatusBucketI18nKey(
	bucket: ListingStatusBucket,
): string {
	switch (bucket) {
		case "live":
			return "realtor.listings.status.live";
		case "draft":
			return "realtor.listings.status.draft";
		case "under_negotiation":
			return "realtor.listings.status.underNegotiation";
		case "sold":
			return "realtor.listings.status.sold";
		case "withdrawn":
			return "realtor.listings.status.withdrawn";
	}
}
